<template>
  <div class="live-chat">
    <div class="messages" ref="messages">
      <div v-for="msg in messages" :key="msg.time + 0" class="message">
        <div class="avatar">
          <router-link :to="{name: 'artist-about', params: {artistId: msg.sender.artist.id }}" v-if="fromArtist(msg)">
            <img :src="msg.sender.artist.avatar_image">
          </router-link>
          <router-link :to="{name: 'fan-profile', params: {id: msg.sender.id }}" v-else>
            <img :src="avatarImage(msg.sender)">
          </router-link>
        </div>
        <div class="info">
          <div v-if="fromArtist(msg)">
            <router-link :to="{name: 'artist-about', params: {artistId: msg.sender.artist.id }}" class="sender">{{ msg.sender.artist.name }}</router-link>
            <span class="time">
              {{ formatTime(msg.time) }}
            </span>
          </div>

          <div v-else>
            <router-link :to="{name: 'fan-profile', params: {id: msg.sender.id }}" class="sender">{{ displayName(msg.sender) }}</router-link>
            <span class="time" v-if="msg.action === 'action' && msg.message.type === 'donation'">
              donated {{ formatTime(msg.time) }}
            </span>
            <span class="time" v-else>
              {{ formatTime(msg.time) }}
            </span>
          </div>

          <div class="text" v-if="msg.action === 'message'" v-html="formatMessage(msg)">
          </div>
          <div class="text donation" v-if="msg.action === 'action' && msg.message.type === 'donation'">
            <img src="/static/icons/donate.svg">
            donated ${{ parseFloat(msg.message.donation.amount).toFixed(2) }}
          </div>
        </div>
        <Report label="Report message" :profile="msg.sender" class="ban" v-if="msg.action === 'message'">
          <LinkButton v-if="isArtist" @click="deleteMessage(msg)">Delete message</LinkButton>
          <LinkButton v-if="isArtist && !isSelf(msg.sender)" @click="banUser(msg.sender)">Block user</LinkButton>
        </Report>
      </div>
    </div>
    <div class="send">
      <b-form-input type="text" size="sm" placeholder="Post message" v-model="message" @keyup.native="onKeyUp" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/custom.scss';

.live-chat {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-left: 0.5em;
  max-width: 20em;

  .header {
    padding: 0.5em 0 0.5em 0;
  }

  .messages {
    height: 0px;
    flex: 1 1 auto;
    overflow-y: scroll;

    &::-webkit-scrollbar {
      width: 10px;
      background-color: #49495b;
    }

    &::-webkit-scrollbar-track {
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 5px;
      background-color: $steel;
      border: 2px solid #49495b;
    }

    .message {
      display: flex;
      font-size: 0.8em;
      padding: 5px;

      &.my {
        background-color: $dark-two;
      }

      .avatar {
        flex: 0 0;
        width: 32px;
        height: 32px;
        flex-basis: 32px;
        margin-right: 5px;
        margin-bottom: 5px;

        display: flex;
        justify-content: center;
        align-items: center;

        img {
          border-radius: 100%;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .info {
        flex-grow: 1;

        .sender {
          font-size: 0.8em;
          font-weight: bold;
          color: $white;
        }

        .time {
          font-size: 0.8em;
          color: $steel;
        }

        .text.donation {
          img {
            width: 1.5em;
            margin-right: 0.5em;
          }

          color: $dark-pink;
        }
      }

      .ban {
        margin-left: 1em;
        cursor: pointer;
        display: block;
        color: $steel;
      }
    }
  }

  .send {
    padding: 0.5em 0.5em 0.5em 0;
  }
}
</style>

<script>
import Vue from 'vue'

import { mapGetters } from 'vuex'
import { profileHelpers } from '@/store/helpers'
import moment from '@/utils/moment'

import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import Report from './Report'
import LinkButton from './LinkButton'

import { linkifyHandles } from '@/utils/textformat'

export default {
  name: 'LiveChat',
  components: {
    FontAwesomeIcon,
    Report,
    LinkButton
  },
  props: {
    live: Object,
    canBan: Boolean
  },
  data () {
    return {
      message: null,
      backlog: [],
      channel: null
    }
  },
  computed: {
    messages () {
      return this.backlog.concat(this.$store.getters['realtime/chatMessages'])
    },
    isArtist () {
      return this.live && this.artist.id === this.live.artist.id
    },
    ...mapGetters('profile', ['currentUser']),
    ...mapGetters('artist', ['artist']),
    ...mapGetters('realtime', ['chatNotify'])
  },
  watch: {
    'live': 'initComponent',
    'messages': 'doScroll',
    'chatNotify': 'onChatNotification'
  },
  mounted () {
    this.initComponent()
  },
  beforeDestroy () {
    this.$store.dispatch('realtime/stopLiveChat')
  },
  methods: {
    async initComponent () {
      this.backlog = await this.$store.dispatch('live/getMessages', {id: this.live.id})

      let cfg = this.live.live_chat_channel
      let artistCfg = this.live.artist_chat_channel
      if (cfg) {
        this.$store.commit('realtime/setConfig', {
          apiKey: cfg.api_key,
          cluster: cfg.cluster
        })
        this.$store.dispatch('realtime/startLiveChat', {
          channelId: cfg.channel,
          artistChannelId: artistCfg ? artistCfg.channel : null
        })
      }

      if (this.isArtist && !artistCfg) {
        console.log('[live-chat] warning: artist channel details not present for artist')
      }
    },
    fromArtist (msg) {
      return (msg.sender.artist && msg.sender.artist.id === this.live.artist.id)
    },
    formatTime (t) {
      return moment.utc(t).fromNow()
    },
    formatMessage (msg) {
      return linkifyHandles(msg.message || '')
    },
    onKeyUp (ev) {
      if (ev.key !== 'Enter') return
      this.sendMessage(this.message)
      this.message = null
    },
    doScroll () {
      Vue.nextTick(() => {
        const el = this.$refs.messages
        el.scrollTop = el.scrollHeight - el.clientHeight
      })
    },
    sendMessage (msg) {
      if (!msg) return
      msg = msg.trim()
      if (!msg) return

      if (this.live.chat_type === 'fan2artist' && !this.isArtist) {
        this.$store.dispatch('realtime/injectOwnMessage', { message: msg })
      }

      this.$store.dispatch('live/sendMessage', {id: this.live.id, message: msg})
    },
    async deleteMessage (msg) {
      if (!this.isArtist) return

      let idx = this.backlog.indexOf(msg)
      if (idx !== -1) this.backlog.splice(idx, 1)

      this.$store.dispatch('realtime/deleteMessage', msg)
      this.$store.dispatch('live/deleteMessage', {
        eventId: this.live.id,
        senderId: msg.sender.id,
        time: msg.time
      })
    },
    isSelf (user) {
      return user && this.live && user.id === this.currentUser.id
    },
    banUser (user) {
      this.$emit('ban', user)
    },
    onChatNotification (payload) {
      if (payload.type === 'block' && payload.profile_id === this.currentUser.id) {
        this.$emit('gotBanned')
      }
    },
    ...profileHelpers
  }
}
</script>



// WEBPACK FOOTER //
// src/components/widgets/LiveChat.vue